import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin } from 'antd'
import { isEmpty, get, cloneDeep, set } from 'lodash'
import { showError, ValidationError, showValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, isReadOnly } from 'helpers/formViews'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_SUBMIT_EVENT } from 'options/events'
import { t } from 'helpers/i18n'
import Select, { Option, getOptionProps } from 'elements/Select'

class FormView extends React.Component {
  state = {}

  componentDidMount() {
    this.fetchSettings()

    Emitter.on(FORM_SUBMIT_EVENT, this.handleSubmit)
  }

  componentWillUnmount() {
    Emitter.off(FORM_SUBMIT_EVENT, this.handleSubmit)
  }

  fetchSettings = async () => {
    try {
      const responses = await Promise.all([
        this.props.newItem(),
        this.props.getSettings({ type: 'createIssueLimitList' }),
        this.props.getLocationGroups(),
        this.props.getLocations(),
      ])

      const item = cloneDeep(responses[0].value.data)

      item.locationId = item.locationId || undefined
      item.locationName = item.locationId ? item.locationName : ''

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        locationGroups: get(responses[2], 'value.data.items', []),
        locations: get(responses[3], 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  handleSubmit = (e) => {
    stopEvent(e)

    this.props.form.validateFields(async (errors, values) => {
      if (!isEmpty(errors)) {
        showValidationError()
        return
      }

      try {
        await this.props.onSave?.(true)

        const response = await this.props.createItem(values)

        await this.props.onSave?.(false)
        await this.props.onSaveAndClose?.(response.value.data)
      } catch (error) {
        showError({ error })
        await this.props.onSave?.(false)
      }
    })
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (name === 'locationGroupId') {
          this.setItemValue('locationId', undefined)

          try {
            const response = await this.props.getLocations({
              locationGroupIds: value ? [value] : [],
            })

            this.setState({ locations: response.value.data.items })
          } catch (error) {
            showError({ error })
          }
        }
      }
    )

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <ValidationError errors={this.state.validationErrors} />
        <Form.Item label={createLabel('name')}>
          {createFieldDecorator('name')(<Input readOnly={readOnly} />)}
        </Form.Item>
        <Form.Item label={t('locationGroup')}>
          <Select onChange={(value) => this.setItemValue('locationGroupId', value)} placeholder={t('all')}>
            {(this.state.locationGroups ?? []).map((each) => (
              <Option key={each.id} value={each.id}>
                <span {...getOptionProps(each)}>{each.displayName}</span>
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label={createLabel('locationId')}>
          {createFieldDecorator('locationId')(
            <Select onChange={(value) => this.setItemValue('locationId', value)} placeholder={t('none')}>
              {(this.state.locations ?? []).map((each) => (
                <Option key={each.id} value={each.id}>
                  <span {...getOptionProps(each)}>{each.displayName}</span>
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Form>
    )
  }
}

export default Form.create()(FormView)
